import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';

import {useApiClient} from '@/hooks';

import {beneficiariesApi} from '../api';
import {trustAccountActivityKeys} from './useTrustAccountActivity';

import type {CreateBeneficiaryPayload} from '@sollapay/types';
import type {QueryClient} from '@tanstack/react-query';

export const beneficiaryKeys = {
  all: (trustAccountId: string) => ['trust-accounts', trustAccountId, 'beneficiaries'] as const,
  lists: (trustAccountId: string) => [...beneficiaryKeys.all(trustAccountId), 'list'] as const
};

/**
 * Shared by create/delete — a beneficiary change never moves the balance/deposit/transaction
 * figures in `stats`, so only the feed and tab-count badge need to refresh (unlike a payment,
 * which invalidates the whole `trustAccountActivityKeys.all()` prefix via `invalidateTrustFinancials`).
 */
function invalidateBeneficiaryCaches(queryClient: QueryClient, trustAccountId: string): void {
  queryClient.invalidateQueries({queryKey: beneficiaryKeys.lists(trustAccountId)});
  queryClient.invalidateQueries({queryKey: trustAccountActivityKeys.feed(trustAccountId)});
  queryClient.invalidateQueries({queryKey: trustAccountActivityKeys.tabCounts(trustAccountId)});
}

export function useBeneficiariesQuery(trustAccountId: string) {
  const client = useApiClient();

  return useQuery({
    queryKey: beneficiaryKeys.lists(trustAccountId),
    queryFn: () => beneficiariesApi.fetchBeneficiaries(client, trustAccountId),
    enabled: !!trustAccountId,
    refetchOnMount: 'always',
    select: response => response.data
  });
}

export function useCreateBeneficiaryMutation(trustAccountId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateBeneficiaryPayload) =>
      beneficiariesApi.createBeneficiary(client, trustAccountId, payload).then(res => res.data),
    onSuccess: () => invalidateBeneficiaryCaches(queryClient, trustAccountId)
  });
}

export function useUpdateBeneficiaryMutation(trustAccountId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({partyId, payload}: {partyId: string; payload: CreateBeneficiaryPayload}) =>
      beneficiariesApi
        .updateBeneficiary(client, trustAccountId, partyId, payload)
        .then(res => res.data),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: beneficiaryKeys.lists(trustAccountId)});
    }
  });
}

export function useDeleteBeneficiaryMutation(trustAccountId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (partyId: string) =>
      beneficiariesApi.deleteBeneficiary(client, trustAccountId, partyId),
    onSuccess: () => invalidateBeneficiaryCaches(queryClient, trustAccountId)
  });
}
